import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { loanFlowService } from '../../services/api';
import { X, Clock } from 'lucide-react';

const MTrustIntro = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [timeLeft, setTimeLeft] = useState(295); // 4:55

  useEffect(() => {
    updateFlow();
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const updateFlow = async () => {
    try {
      const flow = await loanFlowService.getByClient(user.id);
      if (flow) {
        await loanFlowService.update(flow.id, {
          currentStep: 'mtrust_intro'
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleContinue = () => {
    navigate('/client/loan-flow/impots-login');
  };

  const handleClose = () => {
    navigate('/client/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      {/* MiTrust Window */}
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#1e3a5f] p-4">
          <div className="flex items-center justify-between">
            {/* Logos */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <img 
                  src="https://www.mitrust.io/assets/images/logo-white.svg" 
                  alt="MiTrust" 
                  className="h-6"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <span className="hidden text-white font-bold text-lg">MiTrust</span>
              </div>
              <span className="text-white/50">|</span>
              <div className="flex items-center">
                <span className="text-white font-bold text-lg tracking-wide">YOUNITED</span>
              </div>
            </div>
            {/* Timer and Close */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 text-white/80 text-sm">
                <Clock className="h-4 w-4" />
                <span>{formatTime(timeLeft)}</span>
              </div>
              <button 
                onClick={handleClose}
                className="text-white/80 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <div className="flex-1 py-3 text-center bg-white">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 rounded-full bg-[#1e3a5f] text-white text-xs flex items-center justify-center font-bold">1</div>
              <span className="text-sm font-medium text-gray-800">Connexion</span>
            </div>
          </div>
          <div className="flex-1 py-3 text-center bg-gray-50">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 rounded-full bg-gray-300 text-gray-600 text-xs flex items-center justify-center font-bold">2</div>
              <span className="text-sm font-medium text-gray-500">Partage</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Info Text */}
          <p className="text-gray-700 text-sm mb-6">
            MiTrust ne conservera pas vos identifiants. Ils sont transmis 
            directement au site de l'administration fiscale.
          </p>

          {/* What MiTrust will access */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-800 mb-3">MiTrust va accéder aux informations suivantes:</p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span className="text-sm text-gray-700">Nom et prénom</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span className="text-sm text-gray-700">Adresse postale</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span className="text-sm text-gray-700">Avis d'imposition</span>
              </li>
            </ul>
          </div>

          {/* Impots.gouv.fr Section */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3 mb-3">
              <img 
                src="https://www.impots.gouv.fr/themes/minefi/img/Logo-DGFIP_2021.svg" 
                alt="impots.gouv.fr"
                className="h-8"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className="font-semibold text-gray-800">impots.gouv.fr</span>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Connectez-vous avec votre numéro fiscal et votre mot de passe
            </p>
            <Button
              onClick={handleContinue}
              className="w-full text-white font-semibold py-3"
              style={{ backgroundColor: '#8B5CF6' }}
              data-testid="connect-impots-btn"
            >
              Je me connecte à mon compte Impots.gouv.fr
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span className="font-semibold">RGPD</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span className="font-semibold">DSA</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span className="font-semibold">DSP2</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MTrustIntro;
